import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { GeoProvider } from "./contexts/GeoContext";
import SmoothScroll from "./components/SmoothScroll";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import { LegacyCatchAll } from "./components/LegacyRedirects";
import HomePage from "./pages/HomePage";
import ArtistsPage from "./pages/ArtistsPage";
import ShopPage from "./pages/ShopPage";
import ArtworkDetailPage from "./pages/ArtworkDetailPage";
import ArtistProfilePage from "./pages/ArtistProfilePage";
import PictureFramingPage from "./pages/PictureFramingPage";
import ArtConsultationPage from "./pages/ArtConsultationPage";
import NewsPage from "./pages/NewsPage";
import NewsArticlePage from "./pages/NewsArticlePage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import ExhibitionsPage from "./pages/ExhibitionsPage";
// Banksy pages intentionally removed — Banksy references are suppressed
// from the public site (see backend `hide_banksy` startup hook + Header
// nav). `/banksy` and `/banksy/market` now redirect to /shop.
import SundayBMorningPage from "./pages/SundayBMorningPage";
import MarilynMonroePage from "./pages/MarilynMonroePage";
import PricingVatPage from "./pages/PricingVatPage";
import ShippingPage from "./pages/ShippingPage";
import ConsignPage from "./pages/ConsignPage";

// Admin Pages
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/AdminDashboard";
import MediaLibrary from "./components/admin/MediaLibrary";
import ArtworkManager from "./components/admin/ArtworkManager";
import ArtistsManager from "./components/admin/ArtistsManager";
import GalleryManager from "./components/admin/GalleryManager";
import UniversalPageManager from "./components/admin/UniversalPageManager";
import ConsignmentsManager from "./components/admin/ConsignmentsManager";
import SubscribersManager from "./components/admin/SubscribersManager";
import CampaignsManager from "./components/admin/CampaignsManager";
import MissingArtworksManager from "./components/admin/MissingArtworksManager";
import ContactsManager from "./components/admin/ContactsManager";
import ConsultationsManager from "./components/admin/ConsultationsManager";
import NewsManager from "./components/admin/NewsManager";
import ExhibitionsManager from "./components/admin/ExhibitionsManager";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import FramedArtworkDemoPage from "./pages/FramedArtworkDemoPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CookieBanner from "./components/CookieBanner";
import { ContentProtection } from "./components/ContentProtection";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <div className="App bg-background min-h-screen">
      <AuthProvider>
        <GeoProvider>
          <BrowserRouter>
          <SmoothScroll>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/consign" element={<ConsignPage />} />
              <Route path="/artwork/:id" element={<ArtworkDetailPage />} />
              <Route path="/artists" element={<ArtistsPage />} />
              <Route path="/artists/:id" element={<ArtistProfilePage />} />
              <Route path="/artist/:id" element={<ArtistProfilePage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<NewsArticlePage />} />
              <Route path="/picture-framing" element={<PictureFramingPage />} />
              <Route path="/art-consultation" element={<ArtConsultationPage />} />
              <Route path="/exhibitions" element={<ExhibitionsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/banksy" element={<Navigate to="/shop" replace />} />
              <Route path="/banksy/market" element={<Navigate to="/shop" replace />} />
              <Route path="/sunday-b-morning" element={<SundayBMorningPage />} />
              <Route path="/marilyn-monroe" element={<MarilynMonroePage />} />
              <Route path="/pricing-vat" element={<PricingVatPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/framed-artwork-demo" element={<FramedArtworkDemoPage />} />
              
              {/* Admin Login */}
              <Route path="/admin/login" element={<AdminLogin />} />
              
              {/* Protected Admin Routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="artworks" element={<ArtworkManager />} />
                <Route path="artists" element={<ArtistsManager />} />
                <Route path="galleries" element={<GalleryManager />} />
                <Route path="pages" element={<UniversalPageManager />} />
                <Route path="consignments" element={<ConsignmentsManager />} />
                <Route path="mailing-list" element={<SubscribersManager />} />
                <Route path="campaigns" element={<CampaignsManager />} />
                <Route path="missing-artworks" element={<MissingArtworksManager />} />
                <Route path="contacts" element={<ContactsManager />} />
                <Route path="consultations" element={<ConsultationsManager />} />
                <Route path="news" element={<NewsManager />} />
                <Route path="exhibitions" element={<ExhibitionsManager />} />
              </Route>

              {/* Legacy URL fallback — catch unknown paths and route to best match */}
              <Route path="*" element={<LegacyCatchAll />} />
            </Routes>
            <FloatingWhatsApp />
            <CookieBanner />
            <ContentProtection />
            <Toaster richColors closeButton position="top-right" />
          </SmoothScroll>
        </BrowserRouter>
        </GeoProvider>
      </AuthProvider>
    </div>
  );
}

export default App;